import React, { forwardRef } from 'react';
import { Check } from 'lucide-react';
import styles from './Checkbox.module.css';

/**
 * Checkbox Component
 *
 * @param {Object} props
 * @param {string} props.label - Checkbox label
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.size - 'sm' | 'md' | 'lg'
 * @param {string} props.className - Additional CSS classes
 */
const Checkbox = forwardRef(({
  label,
  error,
  helperText,
  disabled = false,
  size = 'md',
  className = '',
  id,
  name,
  checked,
  onChange,
  ...rest
}, ref) => {
  const checkboxId = id || name || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

  const wrapperClasses = [
    styles.wrapper,
    className
  ].filter(Boolean).join(' ');

  const containerClasses = [
    styles.container,
    disabled && styles.disabled
  ].filter(Boolean).join(' ');

  const checkboxClasses = [
    styles.checkbox,
    styles[size],
    error && styles.hasError
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      <label htmlFor={checkboxId} className={containerClasses}>
        <div className={styles.checkboxWrapper}>
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            name={name}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            className={styles.input}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error ? `${checkboxId}-error` : helperText ? `${checkboxId}-helper` : undefined
            }
            {...rest}
          />
          <span className={checkboxClasses}>
            {checked && <Check size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16} className={styles.checkIcon} />}
          </span>
        </div>

        {label && (
          <span className={styles.label}>
            {label}
          </span>
        )}
      </label>

      {error && (
        <p id={`${checkboxId}-error`} className={styles.errorText}>
          {error}
        </p>
      )}

      {!error && helperText && (
        <p id={`${checkboxId}-helper`} className={styles.helperText}>
          {helperText}
        </p>
      )}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;
